import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  FoodPreferencesRec,
  foodPreferencesService,
} from '../../../../services/foodPreferencesInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('FoodPreferencesData');

type FoodPreferencesDataScreenRouteProp = RouteProp<RootStackParamList, 'FoodPreferencesData'>;

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const FoodPreferencesDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<FoodPreferencesDataScreenRouteProp>();
  const [foodPreferencesInfo, setFoodPreferencesInfo] = useState<FoodPreferencesRec | null>(null);
  const [loading, setLoading] = useState(true);

  const foodPreferencesId = route.params?.foodPreferencesId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadFoodPreferencesInfo();
  }, []);

  const loadFoodPreferencesInfo = async () => {
    try {
      if (foodPreferencesId) {
        const info = await foodPreferencesService.getFoodPreferencesById(foodPreferencesId, targetUserId);
        setFoodPreferencesInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await foodPreferencesService.getAllFoodPreferences(targetUserId);
        if (allInfo.length > 0) {
          setFoodPreferencesInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading food preferences info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (foodPreferencesInfo) {
      // Map the data from foodPreferencesInfo to the format expected by FoodPreferencesInputScreen
      const mappedData = {
        id: foodPreferencesInfo.id,
        foodPreferences: foodPreferencesInfo.foodPreferences,
        dietaryRestrictions: foodPreferencesInfo.dietaryRestrictions,
        favoriteDishes: foodPreferencesInfo.favoriteDishes,
        beveragePreferences: foodPreferencesInfo.beveragePreferences,
        specialRequests: foodPreferencesInfo.specialRequests,
      };

      navigation.navigate('FoodPreferencesInput', {
        foodPreferencesData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('FoodPreferencesInput');
    }
  };

  const handleDelete = () => {
    if (foodPreferencesInfo) {
      Alert.alert(
        'Delete Food Preferences',
        'Are you sure you want to delete your food preferences? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await foodPreferencesService.deleteFoodPreferences(foodPreferencesInfo.id);

                // Check if there are any remaining food preferences entries
                const remainingData = await foodPreferencesService.getAllFoodPreferences();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('food_preferences');
                }

                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting food preferences info:', error);
                Alert.alert('Error', 'Failed to delete food preferences');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Food Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Food Preferences Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {foodPreferencesInfo ? (
            <>
              {foodPreferencesInfo.foodPreferences && (
                <InfoItem label="Food Preferences" value={foodPreferencesInfo.foodPreferences} />
              )}
              {foodPreferencesInfo.dietaryRestrictions && (
                <InfoItem label="Dietary Restrictions" value={foodPreferencesInfo.dietaryRestrictions} />
              )}
              {foodPreferencesInfo.favoriteDishes && (
                <InfoItem label="Favorite Dishes" value={foodPreferencesInfo.favoriteDishes} />
              )}
              {foodPreferencesInfo.beveragePreferences && (
                <InfoItem label="Beverage Preferences" value={foodPreferencesInfo.beveragePreferences} />
              )}
              {foodPreferencesInfo.specialRequests && (
                <InfoItem label="Special Requests" value={foodPreferencesInfo.specialRequests} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No food preferences found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Food Preferences</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default FoodPreferencesDataScreen;